import React, { useState, useEffect } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useNotificationSettings } from "hooks/survey";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAILS = 20;

interface NotificationsDialogProps {
  open: boolean;
  surveyId: number;
  onClose: () => void;
}

const NotificationsDialog: React.FC<NotificationsDialogProps> = ({
  open,
  surveyId,
  onClose,
}) => {
  const { settings, loading, error, save, saving } = useNotificationSettings(
    open ? surveyId : null
  );

  const [enabled, setEnabled] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!open) {
      setInitialized(false);
      setEnabled(false);
      setEmails([]);
      setInputValue("");
      setInputError(null);
      setSaveError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!initialized && settings) {
      setEnabled(settings.enabled);
      setEmails(settings.emails);
      setInitialized(true);
    }
  }, [settings, initialized]);

  const addEmail = (raw: string): boolean => {
    const trimmed = raw.trim().toLowerCase();
    if (!trimmed) return false;
    if (!EMAIL_REGEX.test(trimmed)) {
      setInputError(`"${raw}" is not a valid email address`);
      return false;
    }
    if (emails.includes(trimmed)) {
      setInputError(`${trimmed} is already in the list`);
      return false;
    }
    if (emails.length >= MAX_EMAILS) {
      setInputError(`No more than ${MAX_EMAILS} emails are allowed`);
      return false;
    }
    setEmails((prev) => [...prev, trimmed]);
    setInputError(null);
    return true;
  };

  const removeEmail = (email: string) => {
    setEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleSave = async () => {
    setSaveError(null);

    // If there's unsubmitted text in the input box, try to add it first.
    if (inputValue.trim()) {
      if (!addEmail(inputValue)) return;
      setInputValue("");
    }

    if (enabled && emails.length === 0) {
      setSaveError("Add at least one email when notifications are enabled.");
      return;
    }

    try {
      await save(enabled, emails);
      onClose();
    } catch (err) {
      console.error("[NotificationsDialog] Failed to save:", err);
      setSaveError(
        err instanceof Error ? err.message : "Failed to save notification settings"
      );
    }
  };

  const saveDisabled =
    loading || saving || (enabled && emails.length === 0 && !inputValue.trim());

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Notifications</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Send an email to one or more addresses every time a new response is
          submitted. Both the creator and editors share this setting.
        </Typography>

        {loading ? (
          <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 2 }} />
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
              }
              label="Email me when a response is submitted"
              sx={{ mb: 2 }}
            />

            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={[]}
              value={emails}
              disabled={!enabled}
              inputValue={inputValue}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === "reset") {
                  setInputValue("");
                } else {
                  setInputValue(newInputValue);
                  if (inputError) setInputError(null);
                }
              }}
              onChange={(_, newValue, reason) => {
                if (reason === "removeOption" || reason === "clear") {
                  setEmails(newValue as string[]);
                  return;
                }
                if (reason === "createOption") {
                  // User pressed Enter. Take the last raw token and validate it.
                  const latest = (newValue as string[])[newValue.length - 1];
                  if (typeof latest === "string") {
                    addEmail(latest);
                  }
                }
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                    key={option}
                    onDelete={() => removeEmail(option)}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Notification emails"
                  placeholder="Enter an email and press Enter"
                  error={Boolean(inputError)}
                  helperText={
                    inputError ||
                    `${emails.length}/${MAX_EMAILS} emails`
                  }
                />
              )}
            />

            {!enabled && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Toggle notifications on to add or edit recipient emails.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saveDisabled}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationsDialog;
